import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell } from "@phosphor-icons/react";
import api, { fmtDate } from "@/lib/api";

export default function NotificationBell({ onNavigate, placement = "down" }) {
  const { t } = useTranslation();

  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  const loadUnread = () =>
    api
      .get("/notifications/unread-count")
      .then((r) => setUnread(r.data.unread))
      .catch(() => {});

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onDoc);

    return () => {
      document.removeEventListener("mousedown", onDoc);
    };
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);

    if (next) {
      try {
        const { data } = await api.get("/notifications");
        setItems(data);
      } catch {}
    }
  };

  const openItem = async (n) => {
    setOpen(false);

    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        loadUnread();
      } catch {}
    }

    if (n.deep_link && onNavigate) {
      onNavigate(n.deep_link);
    }
  };

  const readAll = async () => {
    try {
      await api.post("/notifications/read-all");

      setItems((prev) =>
        prev.map((n) => ({
          ...n,
          read: true,
        }))
      );

      setUnread(0);
    } catch {}
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={toggle}
        className="p-2 rounded-lg text-slate-400 hover:text-brand-coral hover:bg-brand-coral/10 transition-colors duration-200 relative"
        title={t("notificationBell.heading", {
          defaultValue: "Notifications",
        })}
      >
        <Bell size={18} />

        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-brand-coral text-white text-[9px] font-extrabold grid place-items-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className={`absolute ${placement === "up" ? "left-0 bottom-11" : "right-0 top-11"} w-80 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-2xl shadow-[0_12px_40px_rgb(0,0,0,0.12)] z-[100] overflow-hidden`}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <p className="font-heading font-bold text-sm">
              {t("notificationBell.heading", {
                defaultValue: "Notifications",
              })}
            </p>

            {unread > 0 && (
              <button
                data-testid="notification-read-all"
                onClick={readAll}
                className="text-xs font-semibold text-status-blue hover:underline"
              >
                {t("notificationBell.markAllRead", {
                  defaultValue: "Mark all read",
                })}
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {items.map((n) => (
              <button
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-[#F4F5F7] transition-colors duration-200 ${
                  n.read ? "" : "bg-brand-coral/[0.04]"
                }`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && (
                    <span className="w-2 h-2 rounded-full bg-brand-coral mt-1.5 shrink-0" />
                  )}

                  <div className="min-w-0">
                    <p
                      className={`text-sm break-words ${
                        n.read
                          ? "text-slate-600"
                          : "font-bold text-slate-800"
                      }`}
                    >
                      {n.title}
                    </p>

                    <p className="text-xs text-slate-500 break-words mt-0.5">
                      {n.message}
                    </p>

                    <p className="text-[10px] text-slate-400 mt-1">
                      {fmtDate(n.created_at)}
                    </p>
                  </div>
                </div>
              </button>
            ))}

            {items.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-8">
                {t("notificationBell.noNotifications", {
                  defaultValue: "No notifications yet.",
                })}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}